import React, { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/components/ui/use-toast";
import {
  ChevronRight,
  Edit,
  Trash2,
  Plus,
  FileText,
  Video,
  FileAudio,
  FilePlus,
} from "lucide-react";
import { courseModules, CourseModule, ContentItem } from "@/data/courseContent";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import * as z from "zod";
import { useAuth } from "@/contexts/MyContext";

// Define module form schema
const moduleFormSchema = z.object({
  id: z.string().min(3, {
    message: "ID must be at least 3 characters.",
  }),
  title: z.string().min(2, {
    message: "Title must be at least 2 characters.",
  }),
  description: z.string().min(10, {
    message: "Description must be at least 10 characters.",
  }),
  accessLevel: z.string().min(1, {
    message: "Access level is required.",
  }),
});

// Define content item form schema
const contentItemFormSchema = z.object({
  id: z.string().min(3, {
    message: "ID must be at least 3 characters.",
  }),
  title: z.string().min(2, {
    message: "Title must be at least 2 characters.",
  }),
  description: z.string().min(10, {
    message: "Description must be at least 10 characters.",
  }),
  type: z.enum(["video", "pdf", "audio", "text"], {
    required_error: "Content type is required.",
  }),
  accessLevel: z.string().min(1, {
    message: "Access level is required.",
  }),
  url: z.string().min(1, {
    message: "URL is required.",
  }),
  duration: z.string().optional(),
  thumbnailUrl: z.string().optional(),
});

const ContentManager: React.FC = () => {
  const {getModule, addModule} = useAuth();
  const [modules, setModules] = useState<CourseModule[]>([]);
  const [isAddModuleOpen, setIsAddModuleOpen] = useState(false);
  const [isEditModuleOpen, setIsEditModuleOpen] = useState(false);
  const [isAddItemOpen, setIsAddItemOpen] = useState(false);
  const [isEditItemOpen, setIsEditItemOpen] = useState(false);
  const [currentModule, setCurrentModule] = useState<CourseModule | null>(null);
  const [currentItem, setCurrentItem] = useState<ContentItem | null>(null);
  const [selectedModuleId, setSelectedModuleId] = useState<string | null>(null);
  const { toast } = useToast();
  const { createModule } = useAuth();
  useEffect(()=>{
    (async()=>{
      const module = await getModule();
      setModules(module);
    })()
  },[])
  const moduleForm = useForm<z.infer<typeof moduleFormSchema>>({
    resolver: zodResolver(moduleFormSchema),
    defaultValues: {
      id: "",
      title: "",
      description: "",
      accessLevel: "launch-guide",
    },
  });

  const itemForm = useForm<z.infer<typeof contentItemFormSchema>>({
    resolver: zodResolver(contentItemFormSchema),
    defaultValues: {
      id: "",
      title: "",
      description: "",
      type: "video",
      accessLevel: "launch-guide",
      url: "",
      duration: "",
      thumbnailUrl: "",
    },
  });

  const accessLevels = [
    { id: "launch-guide", name: "Launch Guide" },
    { id: "ai-prompts", name: "AI Prompts" },
    { id: "fast-track", name: "Fast Track" },
    { id: "accelerator", name: "Accelerator" },
    { id: "business-mastery", name: "Business Mastery" },
    { id: "unlimited", name: "Unlimited" },
    { id: "elite-vip", name: "Elite VIP" },
  ];

  const contentTypes = [
    { id: "video", name: "Video" },
    { id: "pdf", name: "PDF Document" },
    { id: "audio", name: "Audio" },
    { id: "text", name: "Text Content" },
  ];

  const handleAddModule = () => {
    setCurrentModule(null);
    moduleForm.reset({
      id: "",
      title: "",
      description: "",
      accessLevel: "launch-guide",
    });
    setIsAddModuleOpen(true);
  };

  const handleEditModule = (module: CourseModule) => {
    setCurrentModule(module);
    moduleForm.reset({
      id: module.id,
      title: module.title,
      description: module.description,
      accessLevel: module.accessLevel,
    });
    setIsEditModuleOpen(true);
  };

  const handleDeleteModule = (moduleId: string) => {
    setModules(modules.filter((module) => module.id !== moduleId));
    toast({
      title: "Module Deleted",
      description: "The course module has been permanently deleted.",
    });
  };

  const handleAddItem = (moduleId: string) => {
    setSelectedModuleId(moduleId);
    setCurrentItem(null);
    
    itemForm.reset({
      id: "",
      title: "",
      description: "",
      type: "video",
      accessLevel:
        modules.find((m) => m.id === moduleId)?.accessLevel || "launch-guide",
      url: "",
      duration: "",
      thumbnailUrl: "",
    });
    setIsAddItemOpen(true);
  };

  const handleEditItem = (moduleId: string, item: ContentItem) => {
    setSelectedModuleId(moduleId);
    setCurrentItem(item);
    itemForm.reset({
      id: item.id,
      title: item.title,
      description: item.description,
      type: item.type,
      accessLevel: item.accessLevel,
      url: item.url,
      duration: item.duration || "",
      thumbnailUrl: item.thumbnailUrl || "",
    });
    setIsEditItemOpen(true);
  };

  const handleDeleteItem = (moduleId: string, itemId: string) => {
    setModules(
      modules.map((module) => {
        if (module.id === moduleId) {
          return {
            ...module,
            items: module.items.filter((item) => item.id !== itemId),
          };
        }
        return module;
      })
    );
    toast({
      title: "Content Item Deleted",
      description: "The content item has been permanently deleted.",
    });
  };

  const onSubmitAddModule = (values: z.infer<typeof moduleFormSchema>) => {
    const newModule: CourseModule = {
      id: values.id,
      title: values.title,
      description: values.description,
      accessLevel: values.accessLevel,
      module_id: selectedModuleId || "",
      items: [],
    };
    createModule(
      values.title,
      values.description,
      values.accessLevel,
      values.id
    );

    setModules([...modules, newModule]);
    setIsAddModuleOpen(false);
    toast({
      title: "Module Added",
      description: `${values.title} has been added successfully.`,
    });
  };

  const onSubmitEditModule = (values: z.infer<typeof moduleFormSchema>) => {
    if (!currentModule) return;

    setModules(
      modules.map((module) => {
        if (module.id === currentModule.id) {
          return {
            ...module,
            id: values.id,
            title: values.title,
            description: values.description,
            accessLevel: values.accessLevel,
          };
        }
        return module;
      })
    );

    setIsEditModuleOpen(false);
    toast({
      title: "Module Updated",
      description: `${values.title} has been updated successfully.`,
    });
  };

  const onSubmitAddItem = async (values: z.infer<typeof contentItemFormSchema>) => {
    if (!selectedModuleId) {
      toast({
        title: "Error",
        description: "No module selected.",
        variant: "destructive",
      });
      return;
    }
  
    try {
      console.log('Adding item:', values);
      await addModule(
        selectedModuleId,
        values.id,
        values.title,
        values.description,
        values.type,
        values.accessLevel,
        values.url,
        values.duration,
        values.thumbnailUrl
      );
  
      const newItem: ContentItem = {
        id: values.id,
        title: values.title,
        description: values.description,
        type: values.type,
        accessLevel: values.accessLevel,
        url: values.url,
        duration: values.duration || undefined,
        thumbnailUrl: values.thumbnailUrl || undefined,
      };
  
      setModules(modules.map(module => 
        module.id === selectedModuleId 
          ? { ...module, items: [...module.items, newItem] } 
          : module
      ));
  
      setIsAddItemOpen(false);
      toast({
        title: "Content Item Added",
        description: `${values.title} has been added successfully.`,
      });
    } catch (error) {
      console.error('Failed to add content item:', error);
      toast({
        title: "Error",
        description: "Failed to add content item. Please try again.",
        variant: "destructive",
      });
    }
  };

  const onSubmitEditItem = (values: z.infer<typeof contentItemFormSchema>) => {
    if (!selectedModuleId || !currentItem) return;

    setModules(
      modules.map((module) => {
        if (module.id === selectedModuleId) {
          return {
            ...module,
            items: module.items.map((item) => {
              if (item.id === currentItem.id) {
                return {
                  ...item,
                  id: values.id,
                  title: values.title,
                  description: values.description,
                  type: values.type,
                  accessLevel: values.accessLevel,
                  url: values.url,
                  duration: values.duration || undefined,
                  thumbnailUrl: values.thumbnailUrl || undefined,
                };
              }
              return item;
            }),
          };
        }
        return module;
      })
    );

    setIsEditItemOpen(false);
    toast({
      title: "Content Item Updated",
      description: `${values.title} has been updated successfully.`,
    });
  };

  const getContentTypeIcon = (type: string) => {
    switch (type) {
      case "video":
        return <Video className="h-4 w-4" />;
      case "audio":
        return <FileAudio className="h-4 w-4" />;
      case "pdf":
        return <FileText className="h-4 w-4" />;
      default:
        return <FileText className="h-4 w-4" />;
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold">Course Content Management</h2>
        <Button onClick={handleAddModule}>
          <Plus className="mr-2 h-4 w-4" /> Add New Module
        </Button>
      </div>

      <div className="space-y-4">
      {modules.map((module) => (
          <div key={module.id} className="border rounded-md overflow-hidden">
            <div className="p-4 bg-slate-50 flex items-center justify-between">
              <div>
                <div className="flex items-center gap-2">
                  <h3 className="text-lg font-semibold">{module.title}</h3>
                  <Badge variant="outline">
                    {accessLevels.find((a) => a.id === module.accessLevel)
                      ?.name || module.accessLevel}
                  </Badge>
                </div>
                <p className="text-sm text-gray-600 mt-1">
                  {module.description}
                </p>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleAddItem(module.module_id)}
                >
                  <Plus className="mr-1 h-4 w-4" /> Add Content
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleEditModule(module)}
                >
                  <Edit className="mr-1 h-4 w-4" /> Edit
                </Button>
                <Button
                  variant="destructive"
                  size="sm"
                  onClick={() => handleDeleteModule(module.id)}
                >
                  <Trash2 className="mr-1 h-4 w-4" /> Delete
                </Button>
              </div>
            </div>
            <div className="divide-y">
              {module.items && module.items?.length === 0 ? (
                <div className="p-6 text-center text-gray-500">
                  No content items in this module.
                  <div className="mt-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleAddItem(module.module_id)}
                    >
                      <Plus className="mr-1 h-4 w-4" /> Add Content Item
                    </Button>
                  </div>
                </div>
               ) : ( 
                  module.items&&module.items.map((item) => (
                   <div
                     key={item.id}
                     className="p-4 flex items-center justify-between hover:bg-gray-50"
                   >
                     <div className="flex items-center gap-3">
                       <div className="bg-blue-100 p-2 rounded-md text-blue-600">
                         {getContentTypeIcon(item.type)}
                       </div>
                       <div>
                         <h4 className="font-medium">{item.title}</h4>
                       <div className="flex items-center gap-2 text-sm text-gray-600">
                           <Badge variant="outline" className="text-xs">
                             {item.type}
                           </Badge>
                           {item.duration && <span>{item.duration}</span>}
                           <span className="text-xs text-gray-400">
                             ({item.accessLevel})
                           </span>
                         </div>
                       </div>
                     </div>
                     <div className="flex gap-2">
                       <Button
                         variant="ghost"
                         size="sm"
                        onClick={() => handleEditItem(module.id, item)}
                       >
                         <Edit className="h-4 w-4" />
                       </Button>
                       <Button
                         variant="ghost"
                         size="sm"
                         className="text-red-500 hover:text-red-600 hover:bg-red-50"
                         onClick={() => handleDeleteItem(module.id, item.id)}
                       >
                         <Trash2 className="h-4 w-4" />
                       </Button>
                     </div>
                   </div>
                ))
              )} 
            </div>
          </div>
        ))}
      </div>

      {/* Add Module Dialog */}
      <Dialog open={isAddModuleOpen} onOpenChange={setIsAddModuleOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add New Module</DialogTitle>
            <DialogDescription>
              Create a new course module to organize your content.
            </DialogDescription>
          </DialogHeader>

          <Form {...moduleForm}>
            <form
              onSubmit={moduleForm.handleSubmit(onSubmitAddModule)}
              className="space-y-4"
            >
              <FormField
                control={moduleForm.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Module ID</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., getting-started" {...field} />
                    </FormControl>
                    <FormDescription>
                      A unique identifier for the module (kebab-case)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={moduleForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Module title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={moduleForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Module description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={moduleForm.control}
                name="accessLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Access Level</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accessLevels.map((level) => (
                          <SelectItem key={level.id} value={level.id}>
                            {level.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="submit">Add Module</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Module Dialog */}
      <Dialog open={isEditModuleOpen} onOpenChange={setIsEditModuleOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Module</DialogTitle>
            <DialogDescription>
              Update the details of this course module.
            </DialogDescription>
          </DialogHeader>

          <Form {...moduleForm}>
            <form
              onSubmit={moduleForm.handleSubmit(onSubmitEditModule)}
              className="space-y-4"
            >
              <FormField
                control={moduleForm.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Module ID</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., getting-started" {...field} />
                    </FormControl>
                    <FormDescription>
                      A unique identifier for the module (kebab-case)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={moduleForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Module title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={moduleForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Module description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={moduleForm.control}
                name="accessLevel"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Access Level</FormLabel>
                    <Select
                      onValueChange={field.onChange}
                      defaultValue={field.value}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select access level" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {accessLevels.map((level) => (
                          <SelectItem key={level.id} value={level.id}>
                            {level.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <DialogFooter>
                <Button type="submit">Update Module</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Add Content Item Dialog */}
      <Dialog open={isAddItemOpen} onOpenChange={setIsAddItemOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Add Content Item</DialogTitle>
            <DialogDescription>
              Add a new content item to this module.
            </DialogDescription>
          </DialogHeader>

          <Form {...itemForm}>
            <form
              onSubmit={itemForm.handleSubmit(onSubmitAddItem)}
              className="space-y-4"
            >
              <FormField
                control={itemForm.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item ID</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., intro-video" {...field} />
                    </FormControl>
                    <FormDescription>
                      A unique identifier for the item (kebab-case)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={itemForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Content title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={itemForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Content description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={itemForm.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {contentTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={itemForm.control}
                  name="accessLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Access Level</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select access level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {accessLevels.map((level) => (
                            <SelectItem key={level.id} value={level.id}>
                              {level.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={itemForm.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="/content/videos/example.mp4"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Path to the content file</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={itemForm.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., 15 min" {...field} />
                      </FormControl>
                      <FormDescription>For video/audio content</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={itemForm.control}
                  name="thumbnailUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Thumbnail URL (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="/content/thumbnails/example.jpg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="submit">Add Content</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>

      {/* Edit Content Item Dialog */}
      <Dialog open={isEditItemOpen} onOpenChange={setIsEditItemOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Edit Content Item</DialogTitle>
            <DialogDescription>
              Update the details of this content item.
            </DialogDescription>
          </DialogHeader>

          <Form {...itemForm}>
            <form
              onSubmit={itemForm.handleSubmit(onSubmitEditItem)}
              className="space-y-4"
            >
              <FormField
                control={itemForm.control}
                name="id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Item ID</FormLabel>
                    <FormControl>
                      <Input placeholder="e.g., intro-video" {...field} />
                    </FormControl>
                    <FormDescription>
                      A unique identifier for the item (kebab-case)
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={itemForm.control}
                name="title"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Title</FormLabel>
                    <FormControl>
                      <Input placeholder="Content title" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                control={itemForm.control}
                name="description"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Description</FormLabel>
                    <FormControl>
                      <Textarea placeholder="Content description" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={itemForm.control}
                  name="type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Content Type</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {contentTypes.map((type) => (
                            <SelectItem key={type.id} value={type.id}>
                              {type.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={itemForm.control}
                  name="accessLevel"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Access Level</FormLabel>
                      <Select
                        onValueChange={field.onChange}
                        defaultValue={field.value}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select access level" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          {accessLevels.map((level) => (
                            <SelectItem key={level.id} value={level.id}>
                              {level.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>
              <FormField
                control={itemForm.control}
                name="url"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Content URL</FormLabel>
                    <FormControl>
                      <Input
                        placeholder="/content/videos/example.mp4"
                        {...field}
                      />
                    </FormControl>
                    <FormDescription>Path to the content file</FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <div className="grid grid-cols-2 gap-4">
                <FormField
                  control={itemForm.control}
                  name="duration"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Duration (Optional)</FormLabel>
                      <FormControl>
                        <Input placeholder="e.g., 15 min" {...field} />
                      </FormControl>
                      <FormDescription>For video/audio content</FormDescription>
                      <FormMessage />
                    </FormItem>
                  )}
                />
                <FormField
                  control={itemForm.control}
                  name="thumbnailUrl"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Thumbnail URL (Optional)</FormLabel>
                      <FormControl>
                        <Input
                          placeholder="/content/thumbnails/example.jpg"
                          {...field}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <DialogFooter>
                <Button type="submit">Update Content</Button>
              </DialogFooter>
            </form>
          </Form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ContentManager;
